import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { JwtService } from '../jwt/jwt.service';
import { AuthenticationService } from '../services/authentication.service';
import { LanguageService } from '../services/language.service';
import { StorageService } from '../utils/storage.service';

@Injectable({
	providedIn: 'root'
})
export class AuthGuard {
	private _router = inject(Router);
	private _storage = inject(StorageService);
	private _auth = inject(AuthenticationService);
	private _jwt = inject(JwtService);
	private _languageService = inject(LanguageService);

	async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
		// Leer y validar la sesión en una sola operación. En una recarga no existe aún
		// estado en memoria; la sesión persistida es la fuente de verdad.
		let jwt = this._storage.getAuthSession();
		const workspace = this._storage.getDataStorage('workspace');
		if (jwt?.access_token) {
			// Validar que el access_token no esté expirado
			try {
				if (this._jwt.isExpiredToken(jwt.access_token)) {
					// Token expirado: intentar refresh si hay refresh_token, esperando a que
					// termine antes de dejar pasar la navegación. Si no esperamos, el primer
					// componente que se monta dispara sus requests con el token todavía vencido
					// (jwt.interceptor.ts toma el token en memoria) y puede terminar deslogueando
					// al usuario sin motivo real.
					if (jwt.refresh_token) {
						this._auth.currentUser = jwt;
						const refreshed = await this._auth.refreshToken();
						if (!refreshed) return false; // signout() ya se encargó de la navegación
						jwt = this._storage.getAuthSession();
						if (!jwt) return false;
					} else {
						this._auth.signout();
						return false;
					}
				}
			} catch {
				// Si el token no se puede decodificar, forzar logout
				this._auth.signout();
				return false;
			}

			jwt.user = this._jwt.decodeToken(jwt.access_token);
			// this._authenticationService.currentUser.user.groups
			if (workspace) {
				jwt.instance_id = workspace.instanceId;
				const data = this._storage.getDataStorage('workspace');
				this._auth.workspace = data;
			}
			this._auth.currentUser = jwt;
			this._auth.selectedRole = this._storage.getDataStorage('role');
			this._auth.expiredTokenInterval(this._auth.currentUser.access_token);
			if (jwt.certero?.access_token) {
				this._auth.expiredCerteroTokenInterval(jwt.certero.access_token);
			}
			this._languageService.setLanguage();
			return true;
		}
		this._router.navigate(['/auth'], { queryParams: { returnUrl: state.url } });
		return false;
	}
}
